import React, { useState, useCallback, useEffect } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  InlineError,
  Button,
  ButtonGroup,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import { CategoriesIcon, ImagesIcon } from "@shopify/polaris-icons";

const AddEditGoal = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");

  const [item, setItem] = useState({
    goal: "",
    owner: "",
    priority: "",
    type: "",
    timeline: "",
    progress: "",
    status: "",
    country: "",
  });

  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/goals/${id}`)
      .then(() => {
        navigate(`/admin/goals`);
      })
      .catch(console.error);
  };

  const validateFields = () => {
    const requiredFields = [
      "goal",
      "owner",
      "priority",
      "type",
      "timeline",
      // "progress",
      "status",
    ];

    const newErrors = {};
    let hasError = false;

    requiredFields.forEach((field) => {
      if (
        item[field] === "" ||
        item[field] === null ||
        item[field] === undefined
      ) {
        hasError = true;
        newErrors[field] = "This field is required";
      } else {
        newErrors[field] = "";
      }
    });

    setErrors(newErrors);
    return !hasError;
  };

  useEffect(() => {
    if (mode === "edit") {
      axios
        .get(`/goals/${id}`)
        .then((result) => {
          const data = result?.data?.data || {};
          setItem({
            goal: data?.goal || "",
            owner: data?.owner || "",
            priority: data?.priority ? String(data?.priority?.id) : "",
            type: data?.type ? String(data?.type?.id) : "",
            timeline: data?.timeline ? String(data?.timeline?.id) : "",
            progress: data?.progress || "",
            status: data?.status ? String(data?.status?.id) : "",
            country: data?.status ? String(data?.country) : "",
          });
          setIsSaving(false);
        })
        .catch((err) => console.log(err));
    }
  }, []);

  const handleSave = () => {
    if (!validateFields()) return;

    setIsSaving(true);
    const formData = new FormData();
    mode === "edit" && formData.append("_method", "PATCH");
    formData.append("goal", item.goal);
    formData.append("owner", item.owner);
    formData.append("priority", item.priority);
    formData.append("type", item.type);
    formData.append("timeline", item.timeline);
    formData.append("progress", item.progress);
    formData.append("status", item.status);
    formData.append("country", item.country);
    axios
      .post(mode === "new" ? "/goals" : `/goals/${id}`, formData)
      .then(() => {
        mode === "new" ? navigate("/admin/goals") : toggleActive();
      })
      .catch((error) => {
        console.log(error.response);
        setIsSaving(false);
        if (error.response && error.response.data?.message) {
          setError(error.response.data.message);
        }
      })
      .finally(() => setIsSaving(false));
  };

  const [optionsCountries, setOptionsCountries] = useState([]);
  useEffect(() => {
    axios
      .get("/settings/countries")
      .then((res) => {
        const countriesData = res?.data?.data || [];
        const options = [
          { label: "All", value: "All" },
          ...countriesData.map((country) => ({
            label: country.name,
            value: country.name,
          })),
        ];
        setOptionsCountries(options);
      })
      .catch((err) => console.log(err));
  }, []);

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Goal`}
      backAction={{ onAction: () => navigate(`/admin/goals`) }}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          <TextField
            label="Goal"
            value={item.goal}
            onChange={(value) => handleChange("goal", value)}
            error={errors.goal}
          />

          <Select
            label="Country"
            options={optionsCountries}
            onChange={(value) => handleChange("country", value)}
            value={item.country}
            placeholder="Please select"
            error={errors?.country}
            styles={{
              // Fixes the overlapping problem of the compont
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
          />

          <TextField
            label="Owner"
            value={item.owner}
            onChange={(value) => handleChange("owner", value)}
            error={errors.owner}
          />

          <Select
            label="Priority"
            options={[
              { label: "P1", value: "1" },
              { label: "P2", value: "2" },
              { label: "P3", value: "3" },
            ]}
            value={item.priority}
            onChange={(value) => handleChange("priority", value)}
            placeholder="Please select"
            error={errors.priority}
          />

          <Select
            label="Type"
            options={[
              { label: "Tactical", value: "1" },
              { label: "Strategic", value: "2" },
              { label: "Marketing", value: "3" },
              { label: "Operational", value: "4" },
              { label: "Technical", value: "5" },
            ]}
            value={item.type}
            onChange={(value) => handleChange("type", value)}
            error={errors.type}
            placeholder="Please select"
          />

          <Select
            label="Timeline"
            options={[
              { label: "This month", value: "1" },
              { label: "3M", value: "2" },
              { label: "6M", value: "3" },
              { label: "12M", value: "4" },
            ]}
            value={item.timeline}
            onChange={(value) => handleChange("timeline", value)}
            placeholder="Please select"
            error={errors.timeline}
          />

          {/* <TextField
            label="Progress"
            type="number"
            value={item.progress}
            onChange={(value) => handleChange("progress", value)}
            error={errors.progress}
          /> */}

          <Select
            label="Status"
            options={[
              { label: "Not Started", value: "1" },
              { label: "In Progress", value: "2" },
              { label: "Blocked", value: "3" },
              { label: "Completed", value: "4" },
            ]}
            value={item.status}
            onChange={(value) => handleChange("status", value)}
            error={errors.status}
            placeholder="Please select"
          />

          <InlineError message={error} />
          {mode === "edit" ? (
            <FormLayout.Group>
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate(`/admin/goals/${id}/images`)}
                icon={ImagesIcon}
              >
                Screenshots
              </Button>
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate(`/admin/goals/${id}/tasks`)}
                icon={CategoriesIcon}
              >
                Subtasks
              </Button>
            </FormLayout.Group>
          ) : null}
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              variant="primary"
              loading={isSaving}
              tone="critical"
              onClick={() => setActiveDelete(true)}
            >
              Delete
            </Button>
          )
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          { content: "No", onAction: toggleDeleteModal, loading: isSaving },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {/* {isSaving && <LoadingScreen />} */}
    </Page>
  );
};

export default AddEditGoal;
